import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { Link } from "react-router-dom";

import { User, Rocket, Home, Trophy, ShoppingCart } from "lucide-react";

const NavbarContainer = styled.nav`
  background: #121212;
  backdrop-filter: blur(10px);
  padding: 0.3rem 0;
  display: flex;
  justify-content: space-around;
  position: fixed;
  bottom: 0;
  max-width: 700px;
  width: 100%;
  z-index: 100;
  height: 80px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  left: 50%;
  transform: translateX(-50%);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  }
`;

const NavItem = styled(Link)<{ $isActive: boolean }>`
  color: ${(props) => (props.$isActive ? "#ccff00" : "#8A8A8E")};
  text-decoration: none;
  font-size: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 500;
  width: 100%;
  padding: 0.5rem 0;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  gap: 4px;
  &:hover {
    color: #ccff00;
    transform: translateY(-2px);

    ${(props) =>
      !props.$isActive &&
      `
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 4px;
        height: 4px;
        background: #ccff00;
        border-radius: 50%;
      }
    `}
  }

  ${(props) =>
    props.$isActive &&
    `
    &::before {
      content: '';
      position: absolute;
      top: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 30px;
      height: 3px;
      background: #ccff00;
      border-radius: 3px;
    }
  `}
`;

const IconWrapper = styled.div<{ $isActive: boolean; $icon: string }>`
  width: 24px;
  height: 24px;
  margin-bottom: 6px;
  background-color: ${(props) => (props.$isActive ? "#ccff00" : "#8A8A8E")};
  mask: url(${(props) => props.$icon}) no-repeat center;
  mask-size: contain;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;

  ${(props) =>
    props.$isActive &&
    `
    &::after {
      content: '';
      position: absolute;
      top: -4px;
      left: -4px;
      right: -4px;
      bottom: -4px;
      border: 2px solid rgba(0, 224, 255, 0.3);
      border-radius: 50%;
    }
  `}

  ${NavItem}:hover & {
    background-color: #ccff00;
    transform: scale(1.1);
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 3px;
  background: #ccff00;
  border-radius: 3px;
`;

const Navbar: React.FC = () => {
  const location = useLocation();

  return (
    <NavbarContainer>
      <NavItem to="/" $isActive={location.pathname === "/"}>
        <Home color={location.pathname === "/" ? "#ccff00" : "#8A8A8E"} />
        Play
      </NavItem>
      <NavItem to="/task" $isActive={location.pathname === "/task"}>
        <Rocket color={location.pathname === "/task" ? "#ccff00" : "#8A8A8E"} />
        Missions
      </NavItem>
      {/* NEW ADDITION: Link to the Store */}
      <NavItem to="/store" $isActive={location.pathname === "/store"}>
        <ShoppingCart
          color={location.pathname === "/store" ? "#ccff00" : "#8A8A8E"}
        />
        Store
      </NavItem>
      <NavItem
        to="/leaderboard"
        $isActive={location.pathname === "/leaderboard"}
      >
        <Trophy
          color={location.pathname === "/leaderboard" ? "#ccff00" : "#8A8A8E"}
        />
        Leaderboard
      </NavItem>
      <NavItem to="/profile" $isActive={location.pathname === "/profile"}>
        <User
          color={location.pathname === "/profile" ? "#ccff00" : "#8A8A8E"}
          className="mb-2"
        />
        Account
      </NavItem>
    </NavbarContainer>
  );
};

export default Navbar;
